/**
 * ProIdContext - Manages Pro account ID for authenticated users
 * 
 * - Upercut users: Need to select which Pro account they are managing
 * - Pro users: Automatically use their own adminProId
 * 
 * This context stores and manages the Pro ID in localStorage for persistence.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface ProIdContextValue {
  // Selected Pro ID for Upercut users
  selectedProId: string | null;
  
  // Actions
  setProId: (proId: string | null) => void;
  clearProId: () => void;
  
  // State
  isProIdRequired: boolean; // True if Upercut user needs to select a Pro
  isProIdSet: boolean; // True if proId is currently set
}

const ProIdContext = createContext<ProIdContextValue | undefined>(undefined);

export const ProIdProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isUpercut, isPro, isAuthenticated, isLoading, user } = useAuth();
  const [selectedProId, setSelectedProIdState] = useState<string | null>(null);
  const hasProInPath = window.location.pathname.includes('/pro/');

  // Initialize proId from localStorage or user data
  useEffect(() => {
    if (isAuthenticated) {
      if (isUpercut) {
        // For Upercut users, load selected Pro from localStorage
        const storedProId = localStorage.getItem('selectedProId');
        if (storedProId) {
          setSelectedProIdState(storedProId);
        }
      }
    }
  }, [isAuthenticated, isUpercut, isPro, user?.userId, user]);

  // Clear proId when user logs out
  // Only clear after auth has finished loading to prevent clearing on initial page load
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setSelectedProIdState(null);
      localStorage.removeItem('selectedProId');
    }
  }, [isAuthenticated, isLoading]);

  // Clear proId
  const clearProId = useCallback(() => {
    localStorage.removeItem('selectedProId');
    setSelectedProIdState(null);
  }, []);

  // Set proId (with persistence)
  const setProId = useCallback((proId: string | null) => {
    if (proId) {
      localStorage.setItem('selectedProId', proId);
      setSelectedProIdState(proId);
    } else {
      clearProId();
    }
  }, [clearProId]);

  const value: ProIdContextValue = {
    selectedProId,
    setProId,
    clearProId,
    isProIdRequired: isUpercut && hasProInPath,
    isProIdSet: !!selectedProId,
  };

  return <ProIdContext.Provider value={value}>{children}</ProIdContext.Provider>;
};

/**
 * Hook to use ProId context
 * Must be used within ProIdProvider
 */
export const useProId = () => {
  const context = useContext(ProIdContext);
  if (!context) {
    throw new Error('useProId must be used within ProIdProvider');
  }
  return context;
};

export default ProIdContext;
